import type { ReactNode } from "react";
import Link from "next/link";

import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "accent" | "ghost";
type Size = "md" | "lg";

const variantClasses: Record<Variant, string> = {
  primary: "bg-navy-800 text-white hover:bg-navy-700 border border-transparent",
  secondary:
    "bg-white text-navy-800 border border-navy-800 hover:bg-navy-50",
  accent:
    "bg-brass-500 text-navy-950 border border-transparent hover:bg-brass-600",
  ghost:
    "bg-transparent text-navy-800 border border-transparent hover:bg-navy-50",
};

const sizeClasses: Record<Size, string> = {
  md: "min-h-[44px] px-5 py-2.5 text-[0.95rem]",
  lg: "min-h-[52px] px-7 py-3 text-base",
};

export type ButtonProps = {
  href?: string;
  external?: boolean;
  variant?: Variant;
  size?: Size;
  className?: string;
  children: ReactNode;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  onClick?: () => void;
  "aria-label"?: string;
};

export function Button({
  href,
  external,
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}: ButtonProps) {
  const classes = cn(
    "inline-flex items-center justify-center gap-2 rounded-md font-semibold tracking-tight transition-colors duration-200",
    "disabled:cursor-not-allowed disabled:opacity-60",
    variantClasses[variant],
    sizeClasses[size],
    className,
  );

  if (href && external) {
    return (
      <a href={href} className={classes} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  if (href) {
    return (
      <Link href={href} className={classes}>
        {children}
      </Link>
    );
  }

  return (
    <button className={classes} {...rest}>
      {children}
    </button>
  );
}
